import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

export class SessionEngine {
  private readonly context: PixelRuntimeContext;

  private unsubscribe: (() => void) | null = null;

  constructor(context: PixelRuntimeContext) {
    this.context = context;
  }

  init(): void {
    if (!this.context.config.modules.core.session.enabled) return;

    this.context.logger.debug('[SessionEngine] Ensuring session');

    const { ttlMs } = this.context.config.modules.core.session;
    this.applySessionLifecycle(ttlMs);

    this.unsubscribe = this.context.services.dispatcher.subscribe(event => {
      if (event.type === 'PAGE_VIEW') {
        this.handlePageViewActivity();
      }
    });
  }

  destroy(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private handlePageViewActivity(): void {
    const { ttlMs, renewOnActivity } = this.context.config.modules.core.session;
    const { session } = this.context.services;

    if (session.isExpired(ttlMs)) {
      this.applySessionLifecycle(ttlMs);
      return;
    }

    if (renewOnActivity) {
      session.touchActivity();
      this.context.logger.debug('[SessionEngine] Session activity renewed');
    }
  }

  private applySessionLifecycle(ttlMs: number): void {
    const result = this.context.services.session.ensureSession(ttlMs);

    if (result.expired && result.previousSessionId) {
      const expiredEvent = this.context.services.eventBuilder.build({
        type: 'SESSION_EXPIRED' as const,
        sessionId: result.previousSessionId,
      });

      this.context.logger.debug('[SessionEngine] Dispatching SESSION_EXPIRED');
      this.context.services.dispatcher.dispatch(expiredEvent);

      const renewedEvent = this.context.services.eventBuilder.build({
        type: 'SESSION_RENEWED' as const,
        sessionId: result.sessionId,
        previousSessionId: result.previousSessionId,
      });

      this.context.logger.debug('[SessionEngine] Dispatching SESSION_RENEWED');
      this.context.services.dispatcher.dispatch(renewedEvent);
    }
  }
}
